import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './context/AuthContext';
import { BrandingProvider } from './context/BrandingContext';
import { VenueProvider } from './context/VenueContext';
import { AppConfigProvider, useAppConfig } from './context/AppConfigContext';
import StaffLogin from './staff/screens/StaffLogin';
import StaffHome from './staff/screens/StaffHome';
import StaffKitchen from './staff/screens/StaffKitchen';
import CustomerCard from './staff/screens/CustomerCard';
import CustomerRegister from './staff/screens/CustomerRegister';
import TicketScanner from './staff/screens/TicketScanner';
import StaffEvents from './staff/screens/StaffEvents';
import StaffEventDetail from './staff/screens/StaffEventDetail';
import AdminLogin from './admin/pages/AdminLogin';
import AdminLayout from './admin/components/AdminLayout';
import AdminOverview from './admin/pages/AdminOverview';
import AdminCustomers from './admin/pages/AdminCustomers';
import AdminPremi from './admin/pages/AdminPremi';
import AdminStaff from './admin/pages/AdminStaff';
import AdminAnalytics from './admin/pages/AdminAnalytics';
import AdminTimeline from './admin/pages/AdminTimeline';
import AdminWalletPreview from './admin/pages/AdminWalletPreview';
import AdminDiagnostics from './admin/pages/AdminDiagnostics';
import AdminBroadcast from './admin/pages/AdminBroadcast';
import AdminCustomerDetail from './admin/pages/AdminCustomerDetail';
import AdminAutomations from './admin/pages/AdminAutomations';
import AdminGiftPoints from './admin/pages/AdminGiftPoints';
import AdminBranding from './admin/pages/AdminBranding';
import AdminPreviewLinks from './admin/pages/AdminPreviewLinks';
import AdminChallenges from './admin/pages/AdminChallenges';
import AdminContacts from './admin/pages/AdminContacts';
import AdminLocale from './admin/pages/AdminLocale';
import AdminProgram from './admin/pages/AdminProgram';
import AdminPromozioni from './admin/pages/AdminPromozioni';
import AdminEvents from './admin/pages/AdminEvents';
import AdminIntegrations from './admin/pages/AdminIntegrations';
import AdminCoupons from './admin/pages/AdminCoupons';
import AdminMarketing from './admin/pages/AdminMarketing';
import AdminStrategy from './admin/pages/AdminStrategy';
import AdminGames from './admin/pages/AdminGames';
import AdminEmailTemplates from './admin/pages/AdminEmailTemplates';
import AdminCampaigns from './admin/pages/AdminCampaigns';
import AdminCampaignsHistory from './admin/pages/AdminCampaignsHistory';
import AdminWhatsAppBot from './admin/pages/AdminWhatsAppBot';
import AdminSocialProofs from './admin/pages/AdminSocialProofs';
import AdminVenues from './admin/pages/AdminVenues';
import AdminImageBuilder from './admin/pages/AdminImageBuilder';
import AdminSpecialMatches from './admin/pages/AdminSpecialMatches';
import AdminTrophies from './admin/pages/AdminTrophies';
import AdminGamesHub from './admin/pages/AdminGamesHub';
import AdminBrand from './admin/pages/AdminBrand';
import AdminPlans from './admin/pages/AdminPlans';
import AdminBilling from './admin/pages/AdminBilling';
import AdminReportBuilder from './admin/pages/AdminReportBuilder';
import AdminSalesAnalytics from './admin/pages/AdminSalesAnalytics';
import AdminPaymentsConfig from './admin/pages/AdminPaymentsConfig';
import AdminTracking from './admin/pages/AdminTracking';
import AdminSecurity from './admin/pages/AdminSecurity';
import AdminDeliveryConfig from './admin/pages/AdminDeliveryConfig';
import AdminRiders from './admin/pages/AdminRiders';
import AdminOrdersAnalytics from './admin/pages/AdminOrdersAnalytics';
import SubscriptionSuccess from './admin/pages/SubscriptionSuccess';
import AdminEventsTickets from './admin/pages/AdminEventsTickets';
import AdminEventLiveDashboard from './admin/pages/AdminEventLiveDashboard';
import AdminShop from './admin/pages/AdminShop';
import AdminMenu from './admin/pages/AdminMenu';
import AdminOrders from './admin/pages/AdminOrders';
import AdminPrintShop from './admin/pages/AdminPrintShop';
import SuperadminLayout from './admin/components/SuperadminLayout';
import SuperadminDashboard from './admin/pages/SuperadminDashboard';
import SuperadminBrands from './admin/pages/SuperadminBrands';
import SuperadminPreviewLinks from './admin/pages/SuperadminPreviewLinks';
import SuperadminPackages from './admin/pages/SuperadminPackages';
import SuperadminBugReports from './admin/pages/SuperadminBugReports';
import SuperadminBilling from './admin/pages/SuperadminBilling';
import SuperadminLazoEmails from './admin/pages/SuperadminLazoEmails';
import SuperadminAgents from './admin/pages/SuperadminAgents';
import SuperadminFeatureToggles from './admin/pages/SuperadminFeatureToggles';
import SuperadminMarketingAssets from './admin/pages/SuperadminMarketingAssets';
import AgentLogin from './admin/pages/AgentLogin';
import AgentPortal from './admin/pages/AgentPortal';
import BrandSignup from './pages/BrandSignup';
import GoogleAuthCallback from './pages/GoogleAuthCallback';
import SuperadminLogin from './admin/pages/SuperadminLogin';
import CustomerDashboard from './pages/CustomerDashboard';
import PublicRegister from './pages/PublicRegister';
import RootLanding from './pages/RootLanding';
import ConsentPage from './pages/ConsentPage';
import CampaignLanding from './pages/CampaignLanding';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import EventTermsPage from './pages/EventTermsPage';
import EventCheckoutPage from './pages/EventCheckoutPage';
import EventCheckoutSuccessPage from './pages/EventCheckoutSuccessPage';
import RewardDetailPage from './pages/CustomerDashboard/RewardDetailPage';
import RiderLogin from './pages/RiderLogin';
import RiderDashboard from './pages/RiderDashboard';
import OrderPage from './pages/OrderPage';
import OrderTrackingPage from './pages/OrderTrackingPage';
import './App.css';

const StaffGuard = ({ children }) => {
  const token = localStorage.getItem('staff_token');
  if (!token) return <Navigate to="/staff/login" replace />;
  return children;
};

const AdminGuard = ({ children }) => {
  const token = localStorage.getItem('admin_token');
  if (!token) return <Navigate to="/admin/login" replace />;
  return children;
};

const SuperadminGuard = ({ children }) => {
  const token = localStorage.getItem('admin_token');
  if (!token) return <Navigate to="/superadmin/login" replace />;
  try {
    const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
    if (user.role !== 'superadmin') return <Navigate to="/superadmin/login" replace />;
  } catch { return <Navigate to="/superadmin/login" replace />; }
  return children;
};

function App() {
  return (
    <AuthProvider>
      <AppConfigProvider>
      <BrandingProvider>
        <BrowserRouter>
          <Toaster
            position="bottom-center"
            toastOptions={{
              duration: 3000,
              style: { fontFamily: 'DM Sans, sans-serif', fontSize: '15px' }
            }}
          />
          <Routes>
            <Route path="/" element={<RootLanding />} />
            <Route path="/register" element={<RootLanding />} />
            <Route path="/staff/login" element={<StaffLogin />} />
            <Route path="/staff/home" element={<StaffGuard><StaffHome /></StaffGuard>} />
            <Route path="/staff/kitchen" element={<StaffGuard><StaffKitchen /></StaffGuard>} />
            <Route path="/staff" element={<Navigate to="/staff/home" replace />} />
            <Route path="/staff/customer/:id" element={<StaffGuard><CustomerCard /></StaffGuard>} />
            <Route path="/staff/register" element={<StaffGuard><CustomerRegister /></StaffGuard>} />
            <Route path="/staff/tickets" element={<StaffGuard><TicketScanner /></StaffGuard>} />
            <Route path="/staff/events" element={<StaffGuard><StaffEvents /></StaffGuard>} />
            <Route path="/staff/events/:id" element={<StaffGuard><StaffEventDetail /></StaffGuard>} />
            <Route path="/my/:token" element={<CustomerDashboard />} />
            <Route path="/order" element={<OrderPage />} />
            <Route path="/menu" element={<OrderPage />} />
            <Route path="/my/order/:orderId" element={<OrderTrackingPage />} />
            <Route path="/my/order-success" element={<OrderTrackingPage />} />
            <Route path="/my/order-cancelled" element={<OrderTrackingPage />} />
            <Route path="/c/:campaignId" element={<CampaignLanding />} />
            <Route path="/register/:businessId" element={<PublicRegister />} />
            <Route path="/consent/:token" element={<ConsentPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/event/termini-e-condizioni" element={<EventTermsPage />} />
            <Route path="/event/:slug/termini" element={<EventTermsPage />} />
            <Route path="/event/:slug/checkout" element={<EventCheckoutPage />} />
            <Route path="/event/:slug/checkout/success" element={<EventCheckoutSuccessPage />} />
            <Route path="/my/:token/premio/:rewardId" element={<RewardDetailPage />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/superadmin/login" element={<SuperadminLogin />} />
            <Route path="/agent/login" element={<AgentLogin />} />
            <Route path="/agent" element={<AgentPortal />} />
            <Route path="/admin/events/:id/live" element={<AdminGuard><AdminEventLiveDashboard /></AdminGuard>} />
            <Route path="/signup" element={<BrandSignup />} />
            <Route path="/auth/google-callback" element={<GoogleAuthCallback />} />
            {/* Rider PWA */}
            <Route path="/rider/login" element={<RiderLogin />} />
            <Route path="/rider/dashboard" element={<RiderDashboard />} />
            <Route path="/rider" element={<Navigate to="/rider/dashboard" replace />} />
            {/* Superadmin routes */}
            <Route path="/superadmin" element={<SuperadminGuard><SuperadminLayout /></SuperadminGuard>}>
              <Route index element={<SuperadminDashboard />} />
              <Route path="brands" element={<SuperadminBrands />} />
              <Route path="preview-links" element={<SuperadminPreviewLinks />} />
              <Route path="packages" element={<SuperadminPackages />} />
              <Route path="bug-reports" element={<SuperadminBugReports />} />
              <Route path="billing" element={<SuperadminBilling />} />
              <Route path="lazo-emails" element={<SuperadminLazoEmails />} />
              <Route path="agents" element={<SuperadminAgents />} />
              <Route path="feature-toggles" element={<SuperadminFeatureToggles />} />
              <Route path="marketing-assets" element={<SuperadminMarketingAssets />} />
            </Route>
            {/* Admin routes */}
            <Route path="/admin" element={<AdminGuard><VenueProvider><AdminLayout /></VenueProvider></AdminGuard>}>
              <Route index element={<AdminOverview />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="customers/:id" element={<AdminCustomerDetail />} />
              <Route path="premi" element={<AdminPremi />} />
              <Route path="staff" element={<AdminStaff />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="timeline" element={<AdminTimeline />} />
              <Route path="wallet-preview" element={<AdminWalletPreview />} />
              <Route path="diagnostics" element={<AdminDiagnostics />} />
              <Route path="sales-analytics" element={<AdminSalesAnalytics />} />
              <Route path="broadcast" element={<AdminBroadcast />} />
              <Route path="automations" element={<AdminAutomations />} />
              <Route path="gift-points" element={<AdminGiftPoints />} />
              <Route path="branding" element={<AdminBranding />} />
              <Route path="preview-links" element={<AdminPreviewLinks />} />
              <Route path="contacts" element={<AdminContacts />} />
              <Route path="locale" element={<AdminLocale />} />
              <Route path="whatsapp-bot" element={<AdminWhatsAppBot />} />
              <Route path="program" element={<AdminProgram />} />
              <Route path="challenges" element={<AdminChallenges />} />
              <Route path="promozioni" element={<AdminPromozioni />} />
              <Route path="events" element={<AdminEvents />} />
              <Route path="integrations" element={<AdminIntegrations />} />
              <Route path="integrations/payments" element={<AdminPaymentsConfig />} />
              <Route path="tracking" element={<AdminTracking />} />
              <Route path="security" element={<AdminSecurity />} />
              <Route path="delivery" element={<AdminDeliveryConfig />} />
              <Route path="riders" element={<AdminRiders />} />
              <Route path="analytics-orders" element={<AdminOrdersAnalytics />} />
              <Route path="coupons" element={<AdminCoupons />} />
              <Route path="marketing" element={<AdminMarketing />} />
              <Route path="strategy" element={<AdminStrategy />} />
              <Route path="games" element={<AdminGamesHub />} />
              <Route path="special-matches" element={<AdminSpecialMatches />} />
              <Route path="trophies" element={<AdminTrophies />} />
              <Route path="shop" element={<AdminShop />} />
              <Route path="menu" element={<AdminMenu />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="print" element={<AdminPrintShop />} />
              <Route path="email-templates" element={<AdminEmailTemplates />} />
              <Route path="campaigns" element={<AdminCampaigns />} />
              <Route path="campaigns/history" element={<AdminCampaignsHistory />} />
              <Route path="social-proofs" element={<AdminSocialProofs />} />
              <Route path="venues" element={<AdminVenues />} />
              <Route path="image-builder" element={<AdminImageBuilder />} />
              <Route path="brand" element={<AdminBrand />} />
              <Route path="plans" element={<AdminPlans />} />
              <Route path="billing" element={<AdminBilling />} />
              <Route path="reports" element={<AdminReportBuilder />} />
              <Route path="subscription-success" element={<SubscriptionSuccess />} />
              <Route path="events-tickets" element={<AdminEventsTickets />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </BrandingProvider>
      </AppConfigProvider>
    </AuthProvider>
  );
}

export default App;
